"use client";

import { useState, useEffect, useRef, useCallback, useMemo, useId } from "react";
import { useTranslation } from "react-i18next";
import { Check, Wallet } from "lucide-react";
import { Tooltip } from "@speakai/ui/tooltip";
import { cn } from "@/lib/utils/cn";
import { usePaymentMethods, useBalance } from "@/features/subscription/payment-queries";
import { useCredits, formatCredits } from "@/features/subscription/hooks/use-credits";
import { PaymentSourceType } from "@/features/subscription/types";
import type { PaymentMethod } from "@/features/subscription/types";
import { cardBrandLabel } from "../utils/card-brand-label";
import { CardBrandIcon } from "./card-brand-icons";
import { ConnectPaypalButton } from "./connect-paypal-button";

interface SelectSourceProps {
  onSelect: (source: {
    id: string;
    type: "card" | "paddle" | "balance";
  }) => void;
  selectedSourceId?: string;
  showBalance?: boolean;
  /** Show a "Connect PayPal" CTA when no paddle source exists. Default false. */
  showPaypalConnect?: boolean;
  canManageBilling?: boolean;
  /** Auto-pick the default (or only) saved method on first mount; balance is
   *  never auto-picked. Skipped if parent passes `selectedSourceId`. */
  autoSelectDefault?: boolean;
  /** Fires only on user-initiated selection (row click or keyboard arrow).
   *  Pass source.type only — do NOT forward raw payment-instrument fields (id/fingerprint/last-four) to analytics. */
  onUserSelect?: (source: { type: "card" | "paddle" | "balance" }) => void;
}

export function SelectSource({
  onSelect,
  selectedSourceId,
  showBalance = true,
  showPaypalConnect = false,
  canManageBilling = true,
  autoSelectDefault = false,
  onUserSelect,
}: SelectSourceProps) {
  const { t } = useTranslation();
  const { data: stripeMethods, isLoading: isStripeLoading, isError: isStripeError, refetch: refetchStripe } = usePaymentMethods(PaymentSourceType.Stripe);
  const { data: paddleData, isLoading: isPaddleLoading, isError: isPaddleError, refetch: refetchPaddle } = usePaymentMethods(PaymentSourceType.Paddle);
  const { data: balanceData, isLoading: isBalanceLoading, isError: isBalanceError, refetch: refetchBalance } = useBalance();
  // Show TOTAL available (plan grant + wallet) — the same number the topbar pill shows,
  // and what the user actually spends. `balance` is the wallet alone.
  const { creditMode, remaining: creditsAvailable } = useCredits();
  const [selectedId, setSelectedId] = useState<string>(
    selectedSourceId ?? "balance"
  );
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // One-shot guard so autoSelectDefault can't re-fire on later refetches.
  const didAutoSelectRef = useRef(false);

  // Two SelectSource instances can mount simultaneously (AddBalance over the
  // payment step) — useId keeps the heading IDs unique.
  const balanceHeadingId = useId();

  useEffect(() => {
    if (selectedSourceId !== undefined) {
      setSelectedId(selectedSourceId);
    }
  }, [selectedSourceId]);

  const balance = balanceData?.balance ?? 0;

  const cardMethods = useMemo(
    () => (stripeMethods ?? []).filter((m: PaymentMethod) => m.type === "card" || m.type === "link"),
    [stripeMethods],
  );
  const paddleMethods = paddleData ?? [];

  const applySelection = useCallback(
    (id: string, type: "card" | "link" | "paddle" | "balance") => {
      setSelectedId(id);
      // Stripe Link rides the card charge rail — collapse it to "card" so
      // SelectSourceProps.onSelect stays stable for existing callers.
      const consumerType: "card" | "paddle" | "balance" =
        type === "link" ? "card" : type;
      onSelect({ id, type: consumerType });
    },
    [onSelect],
  );

  const handleUserSelect = useCallback(
    (id: string, type: "card" | "link" | "paddle" | "balance") => {
      applySelection(id, type);
      const consumerType: "card" | "paddle" | "balance" = type === "link" ? "card" : type;
      onUserSelect?.({ type: consumerType });
    },
    [applySelection, onUserSelect],
  );

  useEffect(() => {
    if (!autoSelectDefault) return;
    if (didAutoSelectRef.current) return;
    if (selectedSourceId !== undefined) return;
    if (isStripeLoading || isPaddleLoading) return;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const isCardExpired = (m: PaymentMethod) =>
      m.type === "card" &&
      m.expYear !== undefined &&
      m.expMonth !== undefined &&
      (m.expYear < currentYear ||
        (m.expYear === currentYear && m.expMonth < currentMonth));

    const candidates: PaymentMethod[] = [
      ...cardMethods.filter((m: PaymentMethod) => !isCardExpired(m)),
      ...paddleMethods,
    ];
    if (candidates.length === 0) return;

    const pick =
      candidates.find((m: PaymentMethod) => m.isDefault) ??
      candidates[0];
    if (!pick) return;

    didAutoSelectRef.current = true;
    // Use applySelection — auto-pick must not fire onUserSelect.
    applySelection(pick.id, pick.type);
  }, [
    autoSelectDefault,
    selectedSourceId,
    isStripeLoading,
    isPaddleLoading,
    cardMethods,
    paddleMethods,
    applySelection,
  ]);

  // Ordered for arrow-key wrap-around — ConnectPaypalButton is outside this cycle.
  const allOptions = useMemo(
    () => [
      ...(showBalance ? [{ id: "balance", type: "balance" as const }] : []),
      ...cardMethods.map((m: PaymentMethod) => ({ id: m.id, type: m.type })),
      ...paddleMethods.map((m: PaymentMethod) => ({ id: m.id, type: m.type })),
    ],
    [showBalance, cardMethods, paddleMethods],
  );

  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(0, allOptions.length);
  }, [allOptions.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      if (allOptions.length === 0) return;
      e.preventDefault();
      const nextIndex =
        e.key === "ArrowDown"
          ? (currentIndex + 1) % allOptions.length
          : (currentIndex - 1 + allOptions.length) % allOptions.length;
      const next = allOptions[nextIndex];
      if (!next) return;
      handleUserSelect(next.id, next.type);
      buttonRefs.current[nextIndex]?.focus();
    },
    [allOptions, handleUserSelect],
  );

  // Fall back to the first rendered option when balance row is hidden.
  const effectiveSelectedId =
    !showBalance && selectedId === "balance" && allOptions.length > 0
      ? allOptions[0].id
      : selectedId;

  if (isStripeLoading || isPaddleLoading || isBalanceLoading) {
    return (
      <div className="space-y-2" data-testid="select-source-loading">
        <div className="h-12 animate-pulse rounded-md bg-muted" />
        <div className="h-12 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }
  if (isStripeError || isPaddleError || isBalanceError) {
    return (
      <div className="rounded-md border border-destructive/30 p-3 text-sm" data-testid="select-source-error" role="alert">
        <p className="text-danger">Failed to load payment methods.</p>
        <button
          type="button"
          onClick={() => {
            if (isStripeError) refetchStripe();
            if (isPaddleError) refetchPaddle();
            if (isBalanceError) refetchBalance();
          }}
          className="mt-1 text-xs text-primary underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" role="radiogroup" aria-label={t("PAYMENT_SETTINGS.PAYMENT_METHOD", "Payment method")}>
      {showBalance && (
        <section aria-labelledby={balanceHeadingId} className="flex flex-col gap-2">
          <h3 id={balanceHeadingId} className="text-sm font-medium text-foreground">
            {t("PAYMENT.SPEAK_BALANCE", "Speak Credit")}
          </h3>
          <button
            type="button"
            role="radio"
            aria-checked={effectiveSelectedId === "balance"}
            tabIndex={effectiveSelectedId === "balance" ? 0 : -1}
            ref={(el) => { buttonRefs.current[0] = el; }}
            onKeyDown={(e) => handleKeyDown(e, 0)}
            className={cn(
              "flex items-center justify-between rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              effectiveSelectedId === "balance"
                ? "border-primary bg-primary/5"
                : "hover:border-muted-foreground/30"
            )}
            onClick={() => handleUserSelect("balance", "balance")}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10" aria-hidden="true">
                {creditMode ? <Wallet className="h-4 w-4" /> : <span className="text-sm font-medium">$</span>}
              </div>
              <div>
                <p className="text-sm font-medium">{t("PAYMENT.SPEAK_BALANCE", "Speak Credit")}</p>
                <p className="text-xs text-muted-foreground">
                  {creditMode ? `${formatCredits(creditsAvailable)} available` : `$${balance.toFixed(2)} available`}
                </p>
              </div>
            </div>
            {effectiveSelectedId === "balance" && (
              <Check className="h-4 w-4 text-primary" aria-hidden="true" />
            )}
          </button>
        </section>
      )}

      {(cardMethods.length > 0 || paddleMethods.length > 0 || showPaypalConnect) && (
      <section aria-label={t("PAYMENT_SETTINGS.PAYMENT_METHOD", "Payment Method")} className="flex flex-col gap-2">
        {cardMethods.map((method: PaymentMethod, i: number) => {
          const idx = showBalance ? i + 1 : i;
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed
          const isExpired =
            method.type === "card" &&
            method.expYear !== undefined &&
            method.expMonth !== undefined &&
            (method.expYear < currentYear ||
              (method.expYear === currentYear && method.expMonth < currentMonth));
          return (
            <Tooltip
              key={method.id}
              content={isExpired ? t("PAYMENT.EXPIRED_TOOLTIP", "This card expired. Update it or add a new card.") : undefined}
            >
              <button
                type="button"
                role="radio"
                aria-checked={effectiveSelectedId === method.id}
                aria-disabled={isExpired ? true : undefined}
                disabled={isExpired}
                tabIndex={effectiveSelectedId === method.id ? 0 : -1}
                ref={(el) => { buttonRefs.current[idx] = el; }}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                className={cn(
                  "flex w-full items-center justify-between rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isExpired
                    ? "cursor-not-allowed opacity-50"
                    : effectiveSelectedId === method.id
                      ? "border-primary bg-primary/5"
                      : "hover:border-muted-foreground/30"
                )}
                onClick={() => {
                  if (isExpired) return;
                  handleUserSelect(method.id, method.type);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted/30 shrink-0" aria-hidden="true">
                    <CardBrandIcon brand={method.type === "link" ? "link" : method.brand} />
                  </div>
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      <span className="truncate">
                        {method.type === "link" ? (
                          cardBrandLabel("link")
                        ) : (
                          <>
                            {cardBrandLabel(method.brand)} **** {method.last4 ?? "••••"}
                          </>
                        )}
                      </span>
                      {method.isDefault && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {t("PAYMENT.DEFAULT_BADGE", "Default")}
                        </span>
                      )}
                      {isExpired && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                          {t("PAYMENT.EXPIRED_BADGE", "Expired")}
                        </span>
                      )}
                    </p>
                    {method.type === "card" && method.expMonth && method.expYear ? (
                      <p className="truncate text-xs text-muted-foreground">
                        Expires {String(method.expMonth ?? "").padStart(2, "0")}/
                        {method.expYear}
                      </p>
                    ) : method.email ? (
                      <p className="truncate text-xs text-muted-foreground" data-sentry-mask>{method.email}</p>
                    ) : null}
                  </div>
                </div>
                {effectiveSelectedId === method.id && !isExpired && (
                  <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                )}
              </button>
            </Tooltip>
          );
        })}

        {paddleMethods.map((method: PaymentMethod, i: number) => {
          const idx = showBalance ? cardMethods.length + i + 1 : cardMethods.length + i;
          return (
            <button
              key={method.id}
              type="button"
              role="radio"
              aria-checked={effectiveSelectedId === method.id}
              tabIndex={effectiveSelectedId === method.id ? 0 : -1}
              ref={(el) => { buttonRefs.current[idx] = el; }}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={cn(
                "flex items-center justify-between rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                effectiveSelectedId === method.id ? "border-primary bg-primary/5" : "hover:border-muted-foreground/30"
              )}
              onClick={() => handleUserSelect(method.id, method.type)}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted/30 shrink-0" aria-hidden="true">
                  <img
                    src="/assets/media/icons/pricing/paypal-icon.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-5 w-auto"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t("PAYMENT.PAYPAL", "PayPal")}</p>
                  {method.email && <p className="truncate text-xs text-muted-foreground" data-sentry-mask>{method.email}</p>}
                </div>
              </div>
              {effectiveSelectedId === method.id && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
            </button>
          );
        })}

        {cardMethods.length === 0 && paddleMethods.length === 0 && !showPaypalConnect && (
          <p className="py-2 text-center text-sm text-muted-foreground">
            {t("PAYMENT.NO_CARD", "No payment methods saved.")}
          </p>
        )}

        {showPaypalConnect && paddleMethods.length === 0 && (
          <ConnectPaypalButton canManageBilling={canManageBilling} layout="row" />
        )}
      </section>
      )}
    </div>
  );
}
